import { AfterTaxRetirementIncomeOptimizerAPIResponse } from '../Types/Autogenerated/AfterTaxRetirementIncomeOptimizerApiResponse';

// Utility function to format currency
export const formatCurrency = (amount: number, showCents: boolean = false): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(amount);
};

// Utility function to format percentage
export const formatPercentage = (value: number, decimalPlaces: number = 1): string => {
  return `${(value * 100).toFixed(decimalPlaces)}%`;
};

// After Tax Retirement Income Summary Generator
export const generateAfterTaxRetirementSummary = (
  apiResponse: AfterTaxRetirementIncomeOptimizerAPIResponse
): string[] => {
  const { current, optimized } = apiResponse;
  const summaryItems: string[] = [];

  // Current vs Optimized comparison
  const improvementAmount = optimized.income_post_tax - current.income_post_tax;
  const improvementPercent = (improvementAmount / current.income_post_tax) * 100;

  if (improvementAmount > 0) {
    summaryItems.push(
      `Tax optimization could increase your after-tax retirement income by ${formatCurrency(improvementAmount)} annually (${improvementPercent.toFixed(1)}% improvement), assuming future tax rates, after accounting for inflation, match current tax rates.`
    );
  } else if (improvementAmount === 0) {
    summaryItems.push(
      `Your current contribution strategy is already tax-optimal for your situation.`
    );
  } else {
    summaryItems.push(
      `Your current strategy appears to be performing well. Minor adjustments may be needed.`
    );
  }

  // Income source breakdown for optimized
  const optimizedTotalPreTaxIncome = optimized.income_roth + optimized.income_trad + optimized.income_brokerage;
  if (optimizedTotalPreTaxIncome > 0) {
    const subjectToTaxPercent = ( (optimized.income_trad + optimized.income_brokerage) / optimizedTotalPreTaxIncome) * 100;

    summaryItems.push(
      `In the optimized scenario, a total of ${subjectToTaxPercent.toFixed(0)}% of your annual income will be subject to taxes`
    );
  }

    // Income source breakdown for optimized
    const currentTotalPreTaxIncome = current.income_roth + current.income_trad + current.income_brokerage;
    if (currentTotalPreTaxIncome > 0) {
      const subjectToTaxPercent = ( (current.income_trad + current.income_brokerage) / currentTotalPreTaxIncome) * 100;
  
      summaryItems.push(
        `In the current scenario, a total of ${subjectToTaxPercent.toFixed(0)}% of your annual income will be subject to taxes`
      );
    }

  // Contribution changes needed
  const contributionChanges: string[] = [];
  
  const optimized_yearly_roth = optimized.yearly_roth_401k + optimized.yearly_roth_ira + optimized.yearly_after_tax_401k  ;
  const current_yearly_roth = current.yearly_roth_401k + current.yearly_roth_ira + current.yearly_after_tax_401k;
  const optimized_yearly_trad = optimized.yearly_trad_401k + optimized.yearly_trad_ira + optimized.yearly_trad_401k_company_match;
  const current_yearly_trad = current.yearly_trad_401k + current.yearly_trad_ira + current.yearly_trad_401k_company_match;
  const optimized_yearly_brokerage = optimized.yearly_brokerage;
  const current_yearly_brokerage = current.yearly_brokerage;
  
  if (Math.abs(optimized_yearly_roth - current_yearly_roth) > 100) {
    const change = optimized_yearly_roth - current_yearly_roth;
    contributionChanges.push(
      `${change > 0 ? 'Increase' : 'Decrease'} Roth contributions from ${formatCurrency(current_yearly_roth)} to ${formatCurrency(optimized_yearly_roth)}`
    );
  }
  
  if (Math.abs(optimized_yearly_trad - current_yearly_trad) > 100) {
    const change = optimized_yearly_trad - current_yearly_trad;
    contributionChanges.push(
      `${change > 0 ? 'Increase' : 'Decrease'} Traditional contributions from ${formatCurrency(current_yearly_trad)} to ${formatCurrency(optimized_yearly_trad)}`
    );
  }
  
  if (Math.abs(optimized_yearly_brokerage - current_yearly_brokerage) > 100) {
    const change = optimized_yearly_brokerage - current_yearly_brokerage;
    contributionChanges.push(
      `${change > 0 ? 'Increase' : 'Decrease'} Brokerage contributions from ${formatCurrency(current_yearly_brokerage)} to ${formatCurrency(optimized_yearly_brokerage)}`
    );
  }

  if (contributionChanges.length > 0) {
    summaryItems.push(
      `To reach the optimal allocation: ${contributionChanges.join(', ')}. These changes should not impact your current take-home pay.`
    );
  }

  // Tax explanation
  summaryItems.push(
    `The "Income Post Tax" amounts shown represent your actual spendable income after applying current tax rates (adjusted for inflation) to Traditional and Brokerage withdrawals, while Roth withdrawals are tax-free.`
  );

  return summaryItems;
};

// Income Tax Summary Generator
export const generateIncomeTaxSummary = (chartData: number[][], grossIncome?: number): string[] => {
  if (!chartData || chartData.length === 0 || !chartData[0]) {
    return [];
  }

  const data = chartData[0]; // Tax components: [Medicare, Social Security, State, Federal, Total]
  const summaryItems: string[] = [];

  // Calculate total tax and effective rate
  // Use the total tax from the API data (last element) instead of summing
  const totalTax = data[data.length - 1]; // Total tax is the last element
  const income = grossIncome || totalTax / 0.25; // Fallback estimate if gross income not provided
  
  if (income > 0 && totalTax > 0) {
    const effectiveRate = (totalTax / income) * 100;
    summaryItems.push(
      `Your effective tax rate is ${effectiveRate.toFixed(1)}% with total taxes of ${formatCurrency(totalTax)} on ${formatCurrency(income)} income.`
    );
  }

  // Find largest tax component (exclude total tax)
  if (data.length > 1) {
    const taxComponents = data.slice(0, -1); // Exclude the total
    const maxTaxIndex = taxComponents.indexOf(Math.max(...taxComponents));
    const taxLabels = ['Medicare', 'Social Security', 'State Income Tax', 'Federal Income Tax'];
    
    if (maxTaxIndex >= 0 && maxTaxIndex < taxLabels.length) {
      summaryItems.push(
        `${taxLabels[maxTaxIndex]} represents your largest tax burden at ${formatCurrency(taxComponents[maxTaxIndex])}.`
      );
    }
  }

  return summaryItems;
};

// Net Worth Summary Generator  
export const generateNetWorthSummary = (
  timeSeriesData: number[][], 
  formData?: {
    yearlyContribution: number;
    growWithInflation: boolean;
    averageAnnualReturn: number;
    inflationRate: number;
  }
): string[] => {
  if (!timeSeriesData || timeSeriesData.length === 0) {
    return [];
  }

  const summaryItems: string[] = [];
  
  // Get final net worth values
  const datasets = timeSeriesData;
  if (datasets.length > 0 && datasets[0].length > 0) {
    const finalValue = datasets[0][datasets[0].length - 1];
    const initialValue = datasets[0][0];
    const totalGrowth = finalValue - initialValue;
    
    summaryItems.push(
      `Your projected net worth will grow to ${formatCurrency(finalValue)} over the investment period.`
    );
    
    if (totalGrowth > 0) {
      summaryItems.push(
        `This represents a total growth of ${formatCurrency(totalGrowth)} from your starting point.`
      );
    }
  }

  // Growth trajectory insight with actual values
  if (formData) {
    const contributionText = formData.growWithInflation 
      ? `growing contributions (starting at ${formatCurrency(formData.yearlyContribution)} annually)` 
      : `consistent contributions of ${formatCurrency(formData.yearlyContribution)} annually`;
    
    summaryItems.push(
      `The chart shows your net worth growth assuming ${contributionText} with ${formatPercentage(formData.averageAnnualReturn / 100)} average annual returns and ${formatPercentage(formData.inflationRate / 100)} inflation.`
    );
  } else {
    summaryItems.push(
      `The chart shows your net worth growth assuming consistent contributions and average annual returns.`
    );
  }

  return summaryItems;
};

// Simplified Retirement Income Summary Generator
export const generateSimplifiedRetirementSummary = (
  timeSeriesData: number[][], 
  withdrawalRate: number
): string[] => {
  if (!timeSeriesData || timeSeriesData.length === 0) {
    return [];
  }

  const summaryItems: string[] = [];
  
  // Get peak and retirement values
  const datasets = timeSeriesData;
  if (datasets.length > 0 && datasets[0].length > 0) {
    const peakValue = Math.max(...datasets[0]);
    const finalValue = datasets[0][datasets[0].length - 1];
    const annualIncome = peakValue * (withdrawalRate / 100);
    
    summaryItems.push(
      `At retirement, you could potentially withdraw ${formatCurrency(annualIncome)} annually using a ${withdrawalRate}% withdrawal rate.`
    );
    
    summaryItems.push(
      `Your retirement account is projected to reach a peak value of ${formatCurrency(peakValue)}.`
    );
    
    if (finalValue < peakValue * 0.8) {
      summaryItems.push(
        `The chart shows your account balance declining over time due to withdrawals. Consider adjusting your withdrawal rate if sustainability is a concern.`
      );
    }
  }

  return summaryItems;
};

// Takehome Pay Summary Generator
export const generateTakehomePaySummary = (chartData: number[][], grossIncome?: number): string[] => {
  if (!chartData || chartData.length === 0 || !chartData[0]) {
    return [];
  }

  const data = chartData[0]; // [Net Pay, Charitable Giving, Federal, Social Security, State, Medicare]
  const summaryItems: string[] = [];

  if (data.length >= 2) {
    const netPay = data[0]; // First element is net pay
    const charitableGiving = data[1];
    const taxes = data.slice(2); // All tax components
    const totalTaxes = taxes.reduce((sum, tax) => sum + tax, 0);
    const totalDeductions = charitableGiving + totalTaxes;
    const grossPay = grossIncome || (netPay + totalDeductions); // Use provided gross or calculate
    
    const takeHomePercent = (netPay / grossPay) * 100;
    
    summaryItems.push(
      `You take home ${takeHomePercent.toFixed(1)}% of your gross pay (${formatCurrency(netPay)} out of ${formatCurrency(grossPay)}).`
    );
    
    summaryItems.push(
      `Total deductions from your paycheck amount to ${formatCurrency(totalDeductions)} (${formatCurrency(totalTaxes)} in taxes + ${formatCurrency(charitableGiving)} in charitable giving).`
    );
  }

  summaryItems.push(
    `This breakdown shows how your gross pay is allocated between taxes, deductions, and take-home pay.`
  );

  return summaryItems;
};